// src/pages/admin/MemberManage.js
// 后台 · 管理中心 · 组织成员（doc01 3.2，缺陷 113/114）
// 结构：左部门导航（所有成员 / 默认部门 + 其他部门 + 新建部门）+ 右侧账号列表。
//  筛选置于表格上方：角色(全部) / 状态(全部·启用·禁用) / 姓名(模糊) + 查询·重置；表上方「添加成员」。
//  列：账号名｜姓名｜角色｜手机号｜部门｜状态｜操作。
//  操作：编辑｜禁用/启用｜生成 token（无独立"删除"，用禁用/启用替代）。
//  规则：系统默认「所有成员」「默认部门」不可改删；其他部门可改名/删除，部门内存在账号则不可删除；
//       管理员 admin 属默认部门并置顶；token 仅生成一次并拼接在账号名后，悬停查看完整 token。
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Select, Input, Modal, Space, Tag, Tooltip, message } from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ApartmentOutlined,
} from '@ant-design/icons';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import {
  H_DEPT_TREE, H_MEMBERS, H_ASSIGN_MODES, H_ROLES,
  STATION_TAGS, H_COMPANIES, STATIONS, PORTAL_REGIONS,
} from '../../config/stationData';

const labelStyle = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };
const stripCount = (t) => String(t).replace(/（.*）/, '');
const genToken = () => `tk_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-6)}`;

const MemberManage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/admin/member');

  const [depts, setDepts] = useState(() => H_DEPT_TREE.map((d) => ({ key: d.key, name: stripCount(d.title), builtin: !!d.builtin })));
  const [members, setMembers] = useState(() => H_MEMBERS.map((m) => ({ ...m })));
  const [activeDept, setActiveDept] = useState('d-all');

  const [fRole, setFRole] = useState(undefined);
  const [fState, setFState] = useState(undefined);
  const [fName, setFName] = useState('');
  const [applied, setApplied] = useState({ role: undefined, state: undefined, name: '' });

  const [deptModal, setDeptModal] = useState({ open: false, key: null, name: '' });
  const [memberModal, setMemberModal] = useState(false);
  const [memberForm, setMemberForm] = useState({ account: '', name: '', phone: '', role: '', dept: '' });
  // 站点分配方式（doc01 3.2）：省市区＝单选下拉；站点标签＝多选；外部公司＝多选；手动选择＝多选（默认全部）
  const [assign, setAssign] = useState({ mode: H_ASSIGN_MODES[0], region: undefined, tags: [], company: [], stations: [] });
  const [editMember, setEditMember] = useState({ open: false, key: null, account: '', name: '', phone: '', role: '', dept: '' });

  const roleOptions = H_ROLES.map((r) => ({ value: r.name, label: r.name }));
  const nonBuiltinDepts = depts.filter((d) => !d.builtin).map((d) => d.name);
  const activeDeptName = (depts.find((d) => d.key === activeDept) || {}).name;
  const byAdminFirst = (a, b) => (b.role === '管理员' ? 1 : 0) - (a.role === '管理员' ? 1 : 0);

  const visibleMembers = useMemo(() => {
    let list = activeDept === 'd-all' ? members : members.filter((m) => m.dept === activeDeptName);
    list = list.filter((m) => {
      if (applied.role && m.role !== applied.role) return false;
      if (applied.state && m.state !== applied.state) return false;
      if (applied.name && !m.name.includes(applied.name.trim())) return false;
      return true;
    });
    return list.slice().sort(byAdminFirst);
  }, [members, activeDept, activeDeptName, applied]);

  const doQuery = () => { setApplied({ role: fRole, state: fState, name: fName }); message.success('已按条件查询'); };
  const doReset = () => { setFRole(undefined); setFState(undefined); setFName(''); setApplied({ role: undefined, state: undefined, name: '' }); message.success('查询条件已重置'); };

  // ---------- 部门 ----------
  const openAddDept = () => setDeptModal({ open: true, key: null, name: '' });
  const openRenameDept = (d) => setDeptModal({ open: true, key: d.key, name: d.name });
  const submitDept = () => {
    const name = deptModal.name.trim();
    if (!name) { message.warning('请输入部门名称'); return; }
    if (depts.some((d) => d.name === name && d.key !== deptModal.key)) { message.warning('部门名称已存在'); return; }
    if (deptModal.key) {
      const oldName = (depts.find((d) => d.key === deptModal.key) || {}).name;
      setDepts((ds) => ds.map((d) => (d.key === deptModal.key ? { ...d, name } : d)));
      setMembers((ms) => ms.map((m) => (m.dept === oldName ? { ...m, dept: name } : m)));
      message.success('部门名称已修改');
    } else {
      setDepts((ds) => [...ds, { key: `d-${Date.now()}`, name, builtin: false }]);
      message.success('部门已新建');
    }
    setDeptModal({ open: false, key: null, name: '' });
  };
  const removeDept = (d) => {
    if (d.builtin) { message.warning('内置部门不可删除'); return; }
    if (members.some((m) => m.dept === d.name)) { message.warning('该部门下存在账号，不可删除'); return; }
    setDepts((ds) => ds.filter((x) => x.key !== d.key));
    if (activeDept === d.key) setActiveDept('d-all');
    message.success('部门已删除');
  };

  // ---------- 成员 ----------
  const openAddMember = () => {
    setMemberForm({ account: '', name: '', phone: '', role: roleOptions[0].value, dept: nonBuiltinDepts[0] || '默认部门' });
    setAssign({ mode: H_ASSIGN_MODES[0], region: undefined, tags: [], company: [], stations: [] });
    setMemberModal(true);
  };
  const submitMember = () => {
    const { account, name, phone, role, dept } = memberForm;
    if (!account.trim() || !name.trim() || !phone.trim() || !role) { message.warning('除部门外均为必填项'); return; }
    if (members.some((m) => m.account === account.trim())) { message.warning('该账号已存在'); return; }
    setMembers((ms) => [...ms, {
      key: `m-${Date.now()}`, account: account.trim(), name: name.trim(), role, phone: phone.trim(),
      dept: dept || '默认部门', state: '启用', assignMode: assign.mode,
    }]);
    setMemberModal(false);
    message.success(`成员「${name.trim()}」已添加`);
  };
  const isLastAdmin = (m) => m.state === '启用' && m.role === '管理员'
    && members.filter((x) => x.state === '启用' && x.role === '管理员').length <= 1;
  // doc01 3.2：点击「禁用」→ 禁用成功、按钮变「启用」；点击「启用」→ 启用成功、按钮变「禁用」
  const toggleMember = (m) => {
    if (m.state === '启用' && isLastAdmin(m)) { message.error('操作失败：需要保留至少一个启用的管理员账号'); return; }
    setMembers((ms) => ms.map((x) => (x.key === m.key ? { ...x, state: x.state === '启用' ? '禁用' : '启用' } : x)));
    message.success(m.state === '启用' ? '禁用成功' : '启用成功');
  };
  const openEditMember = (m) => setEditMember({ open: true, key: m.key, account: m.account, name: m.name, phone: m.phone, role: m.role, dept: m.dept });
  const submitEditMember = () => {
    const m = members.find((x) => x.key === editMember.key);
    if (!editMember.name.trim()) { message.warning('请输入姓名'); return; }
    if (m && editMember.role !== '管理员' && isLastAdmin(m)) { message.error('操作失败：需要保留至少一个启用的管理员账号'); return; }
    setMembers((ms) => ms.map((x) => (x.key === editMember.key
      ? { ...x, name: editMember.name.trim(), phone: editMember.phone.trim(), role: editMember.role, dept: editMember.dept }
      : x)));
    setEditMember((s) => ({ ...s, open: false }));
    message.success('成员信息已更新');
  };
  // doc01 3.2：点击「重置密码」→ 确认弹框（确认重置密码为：Aa123456）→ 确定后提示「重置成功！」
  const resetPwd = () => {
    Modal.confirm({
      title: '重置密码',
      content: '确认重置密码为：Aa123456',
      okText: '确定',
      cancelText: '取消',
      onOk: () => message.success('重置成功！'),
    });
  };
  const genMemberToken = (m) => {
    const token = genToken();
    setMembers((ms) => ms.map((x) => (x.key === m.key ? { ...x, token } : x)));
    message.success(`已为「${m.name}」生成 token（token 仅生成一次，已拼接在账号名后）`);
  };

  // doc01 3.2 生成 token：token 仅生成一次，拼接在账号名后，鼠标悬停查看完整 token
  const accountCell = (r) => {
    const text = r.token ? `${r.account}（${r.token}）` : r.account;
    return (
      <Tooltip title={text} placement="topLeft">
        <span style={{
          color: '#00d4ff', display: 'inline-block', maxWidth: 260,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom',
        }}
        >
          {text}
        </span>
      </Tooltip>
    );
  };

  const deptDisplay = (d) => {
    const count = d.name === '所有成员' ? members.length : members.filter((m) => m.dept === d.name).length;
    return `${d.name}（${count}）`;
  };

  const columns = [
    { title: '账号名', dataIndex: 'account', width: 240, render: (_, r) => accountCell(r) },
    { title: '姓名', dataIndex: 'name', width: 100, render: (v) => <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span> },
    { title: '角色', dataIndex: 'role', width: 130 },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    // doc01 3.2：默认部门的部门为空（列表以「-」呈现）
    { title: '部门', dataIndex: 'dept', width: 100, render: (v) => (v && v !== '默认部门' ? v : <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>) },
    { title: '状态', dataIndex: 'state', width: 90, render: (v) => <Tag color={v === '启用' ? 'green' : 'default'} style={{ margin: 0 }}>{v}</Tag> },
    {
      title: '操作', key: 'op', fixed: 'right', width: 210,
      render: (_, r) => (
        <Space size={2} wrap>
          <Button type="link" size="small" onClick={() => openEditMember(r)}>编辑</Button>
          <Button type="link" size="small" danger={r.state === '启用'} onClick={() => toggleMember(r)}>{r.state === '启用' ? '禁用' : '启用'}</Button>
          {r.token
            ? <Button type="link" size="small" disabled>已生成 token</Button>
            : (
              <Tooltip title="增值收费服务：token 仅生成一次，拼接在账号名后。若公司开放 API 功能被收回，则不展示该操作。">
                <Button type="link" size="small" onClick={() => genMemberToken(r)}>生成 token</Button>
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  const filterRow = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={labelStyle}>角色</span>
        <Select size="small" value={fRole || ''} onChange={(v) => setFRole(v || undefined)} style={{ width: 160 }}
          options={[{ value: '', label: '全部' }, ...roleOptions]} />
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={labelStyle}>状态</span>
        <Select size="small" value={fState || ''} onChange={(v) => setFState(v || undefined)} style={{ width: 120 }}
          options={[{ value: '', label: '全部' }, { value: '启用', label: '启用' }, { value: '禁用', label: '禁用' }]} />
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={labelStyle}>姓名</span>
        <Input size="small" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="输入姓名模糊搜索" prefix={<SearchOutlined />} style={{ width: 180 }} allowClear />
      </span>
      <Space>
        <Button type="primary" size="small" onClick={doQuery}>查询</Button>
        <Button size="small" onClick={doReset}>重置</Button>
      </Space>
    </div>
  );

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '组织成员'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={[]}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16 }}>
        {/* 左：部门导航 */}
        <div className="panel" style={{ alignSelf: 'start' }}>
          <div className="panel-title"><ApartmentOutlined /> 部门</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {depts.map((d) => (
              <div
                key={d.key}
                onClick={() => setActiveDept(d.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  background: activeDept === d.key ? 'rgba(0,180,216,0.16)' : 'transparent',
                  color: activeDept === d.key ? '#00d4ff' : 'rgba(255,255,255,0.78)',
                }}
              >
                <span style={{ flex: 1 }}>{deptDisplay(d)}</span>
                {!d.builtin ? (
                  <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 4 }}>
                    <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openRenameDept(d)} />
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeDept(d)} />
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          <Button block size="small" icon={<PlusOutlined />} style={{ marginTop: 10 }} onClick={openAddDept}>新建部门</Button>
        </div>

        {/* 右：账号列表 */}
        <div className="panel" style={{ minWidth: 0 }}>
          <div className="panel-title">{activeDept === 'd-all' ? '所有成员' : activeDeptName}<small>共 {visibleMembers.length} 个账号</small></div>
          {filterRow}
          <div style={{ marginBottom: 12 }}>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddMember}>添加成员</Button>
          </div>
          <Table rowKey="key" dataSource={visibleMembers} columns={columns} pagination={{ pageSize: 10, size: 'small' }} size="middle" scroll={{ x: 1000 }} />
        </div>
      </div>

      {/* 新建 / 重命名部门 */}
      <Modal
        title={deptModal.key ? '重命名部门' : '新建部门'}
        open={deptModal.open}
        onOk={submitDept}
        onCancel={() => setDeptModal({ open: false, key: null, name: '' })}
        okText="确定" cancelText="取消" destroyOnHidden
      >
        <div style={{ marginBottom: 6, ...labelStyle }}>部门名称</div>
        <Input value={deptModal.name} onChange={(e) => setDeptModal((s) => ({ ...s, name: e.target.value }))} onPressEnter={submitDept} placeholder="请输入部门名称" maxLength={20} />
      </Modal>

      {/* 添加成员 */}
      <Modal title="添加成员" open={memberModal} onOk={submitMember} onCancel={() => setMemberModal(false)} okText="确定" cancelText="取消" width={560} destroyOnHidden>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><div style={labelStyle}>账号名 *</div><Input value={memberForm.account} onChange={(e) => setMemberForm((s) => ({ ...s, account: e.target.value }))} placeholder="请输入登录账号" /></div>
          <div><div style={labelStyle}>姓名 *</div><Input value={memberForm.name} onChange={(e) => setMemberForm((s) => ({ ...s, name: e.target.value }))} placeholder="请输入成员姓名" /></div>
          <div><div style={labelStyle}>手机号 *</div><Input value={memberForm.phone} onChange={(e) => setMemberForm((s) => ({ ...s, phone: e.target.value }))} placeholder="请输入 11 位手机号" maxLength={11} /></div>
          <div><div style={labelStyle}>角色 *</div><Select style={{ width: '100%' }} value={memberForm.role} onChange={(v) => setMemberForm((s) => ({ ...s, role: v }))} options={roleOptions} /></div>
          <div>
            <div style={labelStyle}>部门</div>
            <Select style={{ width: '100%' }} value={memberForm.dept} onChange={(v) => setMemberForm((s) => ({ ...s, dept: v }))}
              options={['默认部门', ...nonBuiltinDepts].map((v) => ({ value: v, label: v }))} />
          </div>
          <div>
            <div style={{ ...labelStyle, marginBottom: 6 }}>站点分配方式（单选，默认省市区）</div>
            <Space wrap>
              {H_ASSIGN_MODES.map((m) => (
                <Button key={m} size="small" type={assign.mode === m ? 'primary' : 'default'} onClick={() => setAssign((s) => ({ ...s, mode: m }))}>{m}</Button>
              ))}
            </Space>
          </div>
          {assign.mode === '省市区' ? (
            <Select allowClear style={{ width: '100%' }} placeholder="选择地区（单选，拥有该地区全部电站权限）" value={assign.region} onChange={(v) => setAssign((s) => ({ ...s, region: v }))} options={PORTAL_REGIONS.map((r) => ({ value: r, label: r }))} />
          ) : null}
          {assign.mode === '站点标签' ? (
            <Select mode="multiple" style={{ width: '100%' }} placeholder="选择站点标签（多选，拥有该标签全部电站权限）" value={assign.tags} onChange={(v) => setAssign((s) => ({ ...s, tags: v }))} options={STATION_TAGS.map((t) => ({ value: t.name, label: t.name }))} />
          ) : null}
          {assign.mode === '外部公司' ? (
            <Select mode="multiple" style={{ width: '100%' }} placeholder="选择外部公司（多选，仅限授权站点）" value={assign.company} onChange={(v) => setAssign((s) => ({ ...s, company: v }))} options={H_COMPANIES.map((c) => ({ value: c.name, label: c.name }))} />
          ) : null}
          {assign.mode === '手动选择' ? (
            <Select mode="multiple" style={{ width: '100%' }} placeholder="手动选择站点（多选，默认全部）" value={assign.stations} onChange={(v) => setAssign((s) => ({ ...s, stations: v }))} options={STATIONS.map((s) => ({ value: s.name, label: s.name }))} />
          ) : null}
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>除部门外均为必填；站点分配决定成员在储能管家内的数据权限。</div>
        </div>
      </Modal>

      {/* 编辑成员 */}
      <Modal title={`编辑用户 · ${editMember.account}`} open={editMember.open} onCancel={() => setEditMember((s) => ({ ...s, open: false }))}
        footer={[
          <Button key="reset" onClick={resetPwd}>重置密码</Button>,
          <Button key="cancel" onClick={() => setEditMember((s) => ({ ...s, open: false }))}>取消</Button>,
          <Button key="ok" type="primary" onClick={submitEditMember}>确定</Button>,
        ]} destroyOnHidden>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><div style={labelStyle}>账号名</div><Input value={editMember.account} disabled /></div>
          <div><div style={labelStyle}>姓名</div><Input value={editMember.name} onChange={(e) => setEditMember((s) => ({ ...s, name: e.target.value }))} /></div>
          <div><div style={labelStyle}>手机号</div><Input value={editMember.phone} onChange={(e) => setEditMember((s) => ({ ...s, phone: e.target.value }))} maxLength={11} /></div>
          <div><div style={labelStyle}>角色</div><Select style={{ width: '100%' }} value={editMember.role} onChange={(v) => setEditMember((s) => ({ ...s, role: v }))} options={roleOptions} /></div>
          <div>
            <div style={labelStyle}>部门</div>
            <Select style={{ width: '100%' }} value={editMember.dept} onChange={(v) => setEditMember((s) => ({ ...s, dept: v }))}
              options={(['默认部门', ...nonBuiltinDepts].includes(editMember.dept) ? ['默认部门', ...nonBuiltinDepts] : [editMember.dept, '默认部门', ...nonBuiltinDepts]).map((v) => ({ value: v, label: v }))} />
          </div>
        </div>
      </Modal>
    </PageSkeleton>
  );
};

export default MemberManage;
